import React from "react";
import { Platform } from "../../types/Platforms";
import {
  extractFacebookReelPermalink,
  extractFacebookReelVideoId,
} from "../../utils/facebookReelsUtils";
import { debug } from "../../utils/debugLogger";

function isPlaceholderFacebookPageId(id: string | undefined): boolean {
  return !id || id.toLowerCase().startsWith("default_");
}

/**
 * Live URL for Facebook Page feed posts: `/{page_id}/posts/{story_id}` for Graph `pageId_postId`.
 * Uses numeric page id from the compound post id when `business_profile_id` is missing or a `default_*` placeholder.
 */
export function buildFacebookLivePostUrl(
  platformPostId: string,
  business_profile_id?: string
): string | undefined {
  const pid = String(platformPostId ?? "").trim();
  if (!pid) return undefined;
  if (pid.startsWith("http://") || pid.startsWith("https://")) return pid;

  const bidRaw = String(business_profile_id ?? "").trim();
  const bid = !isPlaceholderFacebookPageId(bidRaw) ? bidRaw : undefined;

  const fbParts = pid.split("_");
  const fbPostId = fbParts.length > 1 ? fbParts[1] : pid;
  const pageFromCompound =
    fbParts.length > 1 && /^\d+$/.test(fbParts[0]) ? fbParts[0] : undefined;

  const pageForUrl = bid ?? pageFromCompound;
  if (!pageForUrl) return undefined;

  return `https://www.facebook.com/${pageForUrl}/posts/${fbPostId}`;
}

interface LivePostLinkProps {
  platform: string;
  platformPostId: string;
  business_profile_id?: string;
  platform_response?: any; // fixed typo
  /**
   * True when the post carries a PDF/document (LinkedIn document or PDF carousel).
   * Drives the "LinkedIn takes a few minutes to render" hint — the parent knows
   * this from `document_url` / `carousel_slides`, the URN prefix alone doesn't
   * (a `/rest/posts` text post can also come back as `urn:li:ugcPost:`).
   */
  isDocumentPost?: boolean;
}
// Utility function to get the live post URL

function isInstagramFamilyPlatform(platform: string): boolean {
  return String(platform ?? "").toUpperCase().includes("INSTAGRAM");
}

function extractInstagramPermalink(platform_response?: unknown): string | undefined {
  if (!platform_response || typeof platform_response !== "object") return undefined;
  const root = platform_response as Record<string, unknown>;
  const direct = root.permalink;
  if (typeof direct === "string" && direct.trim()) return direct.trim();

  const data = root.data;
  if (data && typeof data === "object") {
    const d = data as Record<string, unknown>;
    if (typeof d.permalink === "string" && d.permalink.trim()) return d.permalink.trim();
  }

  const container = root.container;
  if (container && typeof container === "object") {
    const c = container as Record<string, unknown>;
    if (typeof c.permalink === "string" && c.permalink.trim()) return c.permalink.trim();
  }

  return undefined;
}

function extractInstagramShortcode(platform_response?: unknown): string | undefined {
  if (!platform_response || typeof platform_response !== "object") return undefined;
  const root = platform_response as Record<string, unknown>;
  const sc =
    (typeof root.shortcode === "string" && root.shortcode.trim()) ||
    (typeof root.media_shortcode === "string" && root.media_shortcode.trim());
  return sc || undefined;
}

/**
 * Extracts and formats a YouTube video URL from platform response data
 *
 * @param platform The platform (YOUTUBE or YOUTUBE_SHORTS)
 * @param platformPostId The post ID (may be empty for YouTube)
 * @param platform_response The platform response object from the API
 * @returns A formatted YouTube URL or undefined if no valid ID found
 */
export function getYouTubeUrl(
  platform: string,
  platformPostId?: string,
  platform_response?: any
): string | undefined {
  debug.log(`Getting YouTube URL for ${platform}`, {
    platformPostId,
    hasResponse: !!platform_response,
  });

  // First check if we have a valid platform post ID
  if (platformPostId && platformPostId !== "") {
    debug.log(`Using platform post ID: ${platformPostId}`);
    return `https://www.youtube.com/watch?v=${platformPostId}`;
  }

  // If not, try to extract from platform_response
  if (platform_response) {
    // Check for direct ID field (as seen in the response you provided)
    if (platform_response.id) {
      debug.log(`Using ID from platform_response: ${platform_response.id}`);
      return `https://www.youtube.com/watch?v=${platform_response.id}`;
    }

    // Check for snippet.resourceId.videoId (used in some API responses)
    if (platform_response.snippet?.resourceId?.videoId) {
      debug.log(
        `Using videoId from snippet: ${platform_response.snippet.resourceId.videoId}`
      );
      return `https://www.youtube.com/watch?v=${platform_response.snippet.resourceId.videoId}`;
    }
  }

  debug.log("No valid YouTube ID found");
  return undefined;
}
export function getLivePostUrl(
  platform: string,
  platformPostId: string,
  business_profile_id?: string,
  platform_response?: any
): string | undefined {
  debug.log(`Getting live post URL for platform: ${platform}`);
  debug.log("Platform response:", platform_response);

  if (platform === Platform.FACEBOOK) {
    const url = buildFacebookLivePostUrl(platformPostId, business_profile_id);
    if (url) return url;
  }

  if (platform === Platform.FACEBOOK_REELS) {
    // Reels: Graph video id ≠ page story id — prefer API permalink, then video id, then /reel/{id}, then /posts/…
    const permalink = extractFacebookReelPermalink(platform_response);
    if (permalink) return permalink;

    const videoIdFromResponse = extractFacebookReelVideoId(platform_response);
    if (videoIdFromResponse) {
      return `https://www.facebook.com/reel/${videoIdFromResponse}`;
    }

    const raw = String(platformPostId ?? "").trim();
    if (raw.includes("_")) {
      const fbParts = raw.split("_");
      const second = fbParts.length > 1 ? fbParts[1] : "";
      if (second && /^\d+$/.test(second)) {
        return `https://www.facebook.com/reel/${second}`;
      }
    }

    const feedUrl = buildFacebookLivePostUrl(platformPostId, business_profile_id);
    if (feedUrl) return feedUrl;

    if (/^\d+$/.test(raw)) {
      return `https://www.facebook.com/reel/${raw}`;
    }
  }

  if (isInstagramFamilyPlatform(platform)) {
    const permalink = extractInstagramPermalink(platform_response);
    if (permalink) return permalink;

    const shortcode = extractInstagramShortcode(platform_response);
    if (shortcode) {
      const pU = String(platform ?? "").toUpperCase();
      const path = pU.includes("REELS") ? "reel" : "p";
      return `https://www.instagram.com/${path}/${shortcode}/`;
    }

    const pid = String(platformPostId ?? "").trim();
    if (pid.startsWith("http://") || pid.startsWith("https://")) return pid;
    // Graph media ids are numeric; public URLs need shortcode/permalink — skip numeric-only ids.
    if (pid && !/^\d+$/.test(pid)) {
      const pU = String(platform ?? "").toUpperCase();
      const path = pU.includes("REELS") ? "reel" : "p";
      const code = pid.replace(/^\/+|\/+$/g, "");
      return `https://www.instagram.com/${path}/${code}/`;
    }
  }

  if (platform === "GMB" && platform_response?.searchUrl) {
    debug.log("GMB searchUrl found:", platform_response.searchUrl);
    return platform_response.searchUrl;
  }

  // Use the new utility function for YouTube platforms
  if (platform === Platform.YOUTUBE || platform === Platform.YOUTUBE_SHORTS) {
    return getYouTubeUrl(platform, platformPostId, platform_response);
  }

  // Handle LinkedIn - accept a bare numeric/legacy id or a full URN. Different
  // publish paths return different URN types (urn:li:share: for text/image posts
  // via /rest/posts, urn:li:ugcPost: for document/carousel posts via the
  // Documents API) — hardcoding "share" here double-wraps a ugcPost id into a
  // broken link (urn:li:share:urn:li:ugcPost:...). Pass whatever URN type
  // LinkedIn returned straight through — /feed/update/urn:li:ugcPost:{id}/
  // resolves fine (verified); only bare ids need the legacy urn:li:share: wrap.
  // Note: a fresh document post can take LinkedIn several minutes to finish
  // rendering — until then this URL shows "post cannot be displayed". That is
  // LinkedIn processing lag, not a wrong URL.
  if (platform === Platform.LINKEDIN) {
    const id = (platformPostId ?? platform_response?.id ?? "").toString().trim();
    if (!id) {
      debug.warn("Could not get LinkedIn post id from:", platformPostId);
      return undefined;
    }
    if (id.startsWith("urn:li:")) {
      return `https://www.linkedin.com/feed/update/${id}/`;
    }
    return `https://www.linkedin.com/feed/update/urn:li:share:${id}/`;
  }

  // Log a warning if we can't generate a URL
  debug.warn(`Could not generate live post URL for platform: ${platform}`);
  return undefined;
}

const LivePostLink: React.FC<LivePostLinkProps> = ({
  platform,
  platformPostId,
  business_profile_id,
  platform_response,
  isDocumentPost,
}) => {
  const pid = typeof platformPostId === "string" ? platformPostId : "";
  const liveUrl = getLivePostUrl(platform, pid, business_profile_id, platform_response);

  // Don't render anything if we don't have a valid URL
  if (!liveUrl) {
    debug.log(`No live URL for platform ${platform} with post ID ${pid || "(empty)"}`);
    return null;
  }

  const isIg = isInstagramFamilyPlatform(platform);
  const isFbReel = String(platform).toUpperCase() === Platform.FACEBOOK_REELS;
  return (
    <>
      <a
        href={liveUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-brand-primary underline mt-1 flex items-center gap-1"
      >
        <span className="material-icons text-xs">open_in_new</span>
        {isIg ? "View on Instagram" : isFbReel ? "View on Facebook" : "View Live Post"}
      </a>
      {platform === Platform.LINKEDIN && isDocumentPost && (
        <span className="block text-[11px] text-theme-fg-muted mt-0.5">
          LinkedIn can take a few minutes to finish rendering a document post.
        </span>
      )}
    </>
  );
};

export default LivePostLink;
